import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '../../../lib/supabaseClient';

import SearchBar from '../components/SearchBar';
import LocationTable from '../components/LocationTable';

const lang = 'de';

const localName = (r) =>
  r?.[`name_${lang}`] ||
  r?.name_de ||
  r?.name_en ||
  r?.name_it ||
  r?.name_fr ||
  r?.name_hr ||
  '';

export default function CListPage() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);

  const [userRole, setUserRole] = useState(null);

  const pageSize = 20;

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const { data: auth } = await supabase.auth.getUser();
        const uid = auth?.user?.id;

        if (!uid) {
          if (mounted) setUserRole(null);
          return;
        }

        const { data } = await supabase
          .from('profiles')
          .select('role')
          .eq('id', uid)
          .maybeSingle();

        if (mounted) {
          setUserRole(data?.role || 'user');
        }
      } catch (err) {
        console.error('role load failed', err);
      }
    })();

    return () => {
      mounted = false;
    };
  }, []);

  const isPrivileged =
    userRole === 'admin' ||
    userRole === 'editor';

  // ---------- Kategorien laden ----------
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const u = new URL('/api/categories', window.location.origin);
    u.searchParams.set('lang', lang);

    fetch(u.toString())
      .then((r) => (r.ok ? r.json() : Promise.reject(r)))
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch((err) => {
        console.error('load categories failed', err);
        setCategories([]);
      });
  }, []);

  // ---------- Mapping: category_id -> Label ----------
  const categoryNameById = useMemo(() => {
    const idsByNorm = {};
    const labelByNorm = {};

    for (const c of categories) {
      const label = (c.name_de || c.name_en || `#${c.id}`).trim();
      const norm = label.toLowerCase();

      if (!idsByNorm[norm]) idsByNorm[norm] = [];

      idsByNorm[norm].push(c.id);

      if (!labelByNorm[norm]) {
        labelByNorm[norm] = label;
      }
    }

    const m = {};

    Object.entries(idsByNorm).forEach(([norm, ids]) => {
      const label = labelByNorm[norm];

      ids.forEach((id) => {
        m[id] = label;
      });
    });

    return m;
  }, [categories]);

  // ---------- Liste laden ----------
  async function load() {
    setLoading(true);

    try {
      const u = new URL('/api/locations', window.location.origin);

      u.searchParams.set('page', String(page));
      u.searchParams.set('limit', String(pageSize));
      u.searchParams.set('lang', lang);

      if (q) {
        u.searchParams.set('q', q);
      }

      const res = await fetch(u.toString());

      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }

      const json = await res.json();

      const rows = (json.items || []).map((it) => {
        const name = localName(it);
        const category_label =
          categoryNameById[it.category_id] ||
          `#${it.category_id}`;

        return {
          ...it,
          name,
          category_label,
        };
      });

      setItems(rows);
      setTotal(json.total || 0);
    } catch (err) {
      console.error('load locations failed', err);
      setItems([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [page, categoryNameById]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      load();
    }, 300);

    return () => clearTimeout(t);
  }, [q]);

  const maxPage = Math.max(
    1,
    Math.ceil(total / pageSize)
  );

  return (
    <div
      style={{
        maxWidth: 1100,
        margin: '0 auto',
        padding: 16,
        position: 'relative',
      }}
    >
      {isPrivileged && (
        <div
          style={{
            position: 'absolute',
            top: 16,
            right: 16,
          }}
        >
          <Link
            to="/"
            style={{
              display: 'inline-block',
              background: '#0f1626',
              color: '#fff',
              borderRadius: 8,
              padding: '.45rem .8rem',
              textDecoration: 'none',
            }}
          >
            ← Menü
          </Link>
        </div>
      )}

      <h1
        style={{
          fontSize: 22,
          margin: '8px 0 16px',
        }}
      >
        Modul C · Redaktion
      </h1>

      <SearchBar
        value={q}
        onChange={setQ}
        onClear={() => setQ('')}
      />

      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          margin: '10px 0 8px',
        }}
      >
        <div>
          {loading
            ? 'Lade…'
            : `Seite ${page}/${maxPage} · ${total} Einträge`}
        </div>

        <div
          style={{
            display: 'flex',
            gap: 12,
            alignItems: 'center',
          }}
        >
          <button
            disabled={page <= 1 || loading}
            onClick={() =>
              setPage((p) => Math.max(1, p - 1))
            }
          >
            Zurück
          </button>

          <button
            disabled={page >= maxPage || loading}
            onClick={() =>
              setPage((p) => Math.min(maxPage, p + 1))
            }
          >
            Weiter
          </button>
        </div>
      </div>

      <LocationTable
        rows={items}
        categoryNameById={categoryNameById}
      />
    </div>
  );
}